import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  PlayArrow as ConnectIcon,
  Stop as DisconnectIcon,
  DesktopWindows as DesktopIcon,
  Circle as StatusIcon,
  CloudQueue as CloudIcon,
  Fullscreen as FullscreenIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import Guacamole from 'guacamole-common-js';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';

interface ConnectionInfo {
  tunnelUrl: string;
  serverIp: string;
  rdpPort: number;
  serverName: string;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'disconnecting';

interface RdpSettings {
  resolution: string;
  quality: string;
  audio: boolean;
  wallpaper: boolean;
}

const RESOLUTION_OPTIONS = [
  { value: 'auto', label: 'resolutionAuto' },
  { value: '1920x1080', label: '1920 × 1080' },
  { value: '1600x900', label: '1600 × 900' },
  { value: '1366x768', label: '1366 × 768' },
  { value: '1280x720', label: '1280 × 720' },
  { value: '1024x768', label: '1024 × 768' },
];

const DEFAULT_SETTINGS: RdpSettings = {
  resolution: 'auto',
  quality: 'high',
  audio: true,
  wallpaper: false,
};

function loadSettings(): RdpSettings {
  try {
    const saved = localStorage.getItem('rdp_settings');
    if (saved) return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
  } catch (_) { /* ignore */ }
  return DEFAULT_SETTINGS;
}

function saveSettings(settings: RdpSettings) {
  localStorage.setItem('rdp_settings', JSON.stringify(settings));
}

const Desktop: React.FC = () => {
  const { t } = useTranslation();
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [rdpSettings, setRdpSettings] = useState<RdpSettings>(loadSettings);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const displayRef = useRef<HTMLDivElement>(null);
  const clientRef = useRef<Guacamole.Client | null>(null);
  const keyboardRef = useRef<Guacamole.Keyboard | null>(null);
  const resizeHandlerRef = useRef<(() => void) | null>(null);
  const lastScaleRef = useRef<number>(0);
  const resizingRef = useRef<boolean>(false);
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSnackbar = useCallback(
    (message: string, severity: 'success' | 'error' | 'info') => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const updateSettings = useCallback((patch: Partial<RdpSettings>) => {
    setRdpSettings(prev => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const fetchConnectionInfo = async () => {
      try {
        setLoading(true);
        const response = await api.get<ConnectionInfo>('/user/connection-info');
        setConnectionInfo(response.data);
      } catch (err: any) {
        showSnackbar(
          err.response?.data?.message || t('desktop.fetchFailed'),
          'error'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchConnectionInfo();

    return () => {
      // Cleanup on unmount
      if (clientRef.current) {
        clientRef.current.disconnect();
      }
      cleanup();
    };
  }, [showSnackbar, t]);

  const handleConnect = async () => {
    if (!connectionInfo) return;

    setConnectionState('connecting');
    try {
      // Start session in backend
      const sessionRes = await api.post('/user/session/start');
      setSessionId(sessionRes.data.sessionId);

      // Resolve resolution
      let width: number;
      let height: number;
      if (rdpSettings.resolution === 'auto') {
        width = window.screen.width;
        height = window.screen.height;
      } else {
        const [w, h] = rdpSettings.resolution.split('x').map(Number);
        width = w;
        height = h;
      }

      // Build WebSocket URL with settings as query params
      const token = localStorage.getItem('token');
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const { hostname, port } = window.location;
      const baseHost = port === '3000' ? `${hostname}:8080` : `${hostname}${port ? ':' + port : ''}`;
      const settingsParams = `&width=${width}&height=${height}&dpi=96&quality=${rdpSettings.quality}&audio=${rdpSettings.audio}&wallpaper=${rdpSettings.wallpaper}`;
      const tunnelUrl = `${wsProtocol}//${baseHost}/ws/tunnel?token=${encodeURIComponent(token || '')}${settingsParams}`;

      // Create Guacamole tunnel and client
      const tunnel = new Guacamole.WebSocketTunnel(tunnelUrl);
      const client = new Guacamole.Client(tunnel);
      clientRef.current = client;

      // State change handler
      client.onstatechange = (state: number) => {
        switch (state) {
          case Guacamole.Client.State.CONNECTED:
            setConnectionState('connected');
            showSnackbar(t('desktop.sessionStarted'), 'success');
            break;
          case Guacamole.Client.State.DISCONNECTED:
            setConnectionState('disconnected');
            cleanup();
            break;
          case Guacamole.Client.State.CONNECTING:
          case Guacamole.Client.State.WAITING:
            setConnectionState('connecting');
            break;
        }
      };

      // Error handler
      client.onerror = (status: Guacamole.Status) => {
        showSnackbar(t('desktop.connectionError'), 'error');
        setConnectionState('disconnected');
        cleanup();
      };

      tunnel.onerror = (status: Guacamole.Status) => {
        showSnackbar(t('desktop.connectionError'), 'error');
        setConnectionState('disconnected');
        cleanup();
      };

      // Mount display
      if (displayRef.current) {
        displayRef.current.innerHTML = '';
        const display = client.getDisplay();
        const element = display.getElement();
        element.style.cursor = 'default';
        displayRef.current.appendChild(element);

        // Scale display to fit container (with loop guard)
        const resizeDisplay = () => {
          if (!displayRef.current || resizingRef.current) return;
          resizingRef.current = true;

          const containerWidth = displayRef.current.offsetWidth;
          const containerHeight = displayRef.current.offsetHeight;
          const displayWidth = display.getWidth();
          const displayHeight = display.getHeight();

          if (displayWidth > 0 && displayHeight > 0) {
            const scale = Math.min(
              containerWidth / displayWidth,
              containerHeight / displayHeight
            );
            // Only apply if scale actually changed (prevents loop)
            if (Math.abs(scale - lastScaleRef.current) > 0.001) {
              lastScaleRef.current = scale;
              display.scale(scale);
            }
          }

          resizingRef.current = false;
        };

        // Server-side resolution changes trigger onresize
        display.onresize = () => {
          lastScaleRef.current = 0; // force recalc on server resize
          resizeDisplay();
        };

        // Debounced window resize handler
        const debouncedResize = () => {
          if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
          resizeTimerRef.current = setTimeout(resizeDisplay, 150);
        };

        resizeHandlerRef.current = debouncedResize;
        window.addEventListener('resize', debouncedResize);

        // Setup mouse
        const mouse = new Guacamole.Mouse(element);
        mouse.onmousedown = mouse.onmouseup = mouse.onmousemove = (state: Guacamole.Mouse.State) => {
          client.sendMouseState(state);
        };

        // Setup keyboard
        const keyboard = new Guacamole.Keyboard(document);
        keyboardRef.current = keyboard;
        keyboard.onkeydown = (keysym: number) => {
          client.sendKeyEvent(true, keysym);
          return true;
        };
        keyboard.onkeyup = (keysym: number) => {
          client.sendKeyEvent(false, keysym);
        };
      }

      // Connect
      client.connect();

    } catch (err: any) {
      setConnectionState('disconnected');
      showSnackbar(
        err.response?.data?.message || t('desktop.sessionStartFailed'),
        'error'
      );
    }
  };

  const cleanup = () => {
    if (keyboardRef.current) {
      keyboardRef.current.onkeydown = null;
      keyboardRef.current.onkeyup = null;
      keyboardRef.current = null;
    }
    if (resizeHandlerRef.current) {
      window.removeEventListener('resize', resizeHandlerRef.current);
      resizeHandlerRef.current = null;
    }
    if (resizeTimerRef.current) {
      clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = null;
    }
    lastScaleRef.current = 0;
    clientRef.current = null;
    if (displayRef.current) {
      displayRef.current.innerHTML = '';
    }
  };

  const handleDisconnect = async () => {
    setConnectionState('disconnecting');
    try {
      if (clientRef.current) {
        clientRef.current.disconnect();
      }
      if (sessionId) {
        await api.post(`/user/session/end?sessionId=${sessionId}`);
      }
      setConnectionState('disconnected');
      setSessionId(null);
      cleanup();
      showSnackbar(t('desktop.sessionEnded'), 'info');
    } catch (err: any) {
      setConnectionState('disconnected');
      cleanup();
    }
  };

  const handleFullscreen = () => {
    if (displayRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        displayRef.current.requestFullscreen();
      }
    }
  };

  const getStatusColor = (): string => {
    switch (connectionState) {
      case 'connected':
        return '#4caf50';
      case 'connecting':
      case 'disconnecting':
        return '#ff9800';
      case 'disconnected':
        return '#9e9e9e';
    }
  };

  const getStatusLabel = (): string => {
    switch (connectionState) {
      case 'connected':
        return t('desktop.connected');
      case 'connecting':
        return t('desktop.connecting');
      case 'disconnecting':
        return t('desktop.disconnecting');
      case 'disconnected':
        return t('desktop.disconnected');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          backgroundColor: '#1a1a2e',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: '#64b5f6', mb: 2 }} />
          <Typography variant="body1" sx={{ color: '#90a4ae' }}>
            {t('desktop.loadingConnection')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1a1a2e',
        width: '100%',
        height: '100vh',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          backgroundColor: '#16213e',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DesktopIcon sx={{ color: '#64b5f6' }} />
          <Typography variant="subtitle1" sx={{ color: '#e3f2fd', fontWeight: 600 }}>
            {connectionInfo?.serverName || t('desktop.title')}
          </Typography>
          <Chip
            icon={<StatusIcon sx={{ fontSize: '12px !important', color: `${getStatusColor()} !important` }} />}
            label={getStatusLabel()}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#e0e0e0',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {connectionState === 'connected' && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleFullscreen}
              startIcon={<FullscreenIcon />}
              sx={{
                textTransform: 'none',
                color: '#90a4ae',
                borderColor: 'rgba(255,255,255,0.2)',
                '&:hover': { borderColor: '#64b5f6', color: '#64b5f6' },
              }}
            >
              {t('desktop.fullscreen')}
            </Button>
          )}
          {connectionState === 'disconnected' && (
            <Button
              variant="contained"
              startIcon={<ConnectIcon />}
              onClick={handleConnect}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#43a047' },
              }}
            >
              {t('desktop.connect')}
            </Button>
          )}
          {connectionState === 'connecting' && (
            <Button
              variant="contained"
              disabled
              size="small"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              {t('desktop.connecting')}
            </Button>
          )}
          {connectionState === 'connected' && (
            <Button
              variant="contained"
              startIcon={<DisconnectIcon />}
              onClick={handleDisconnect}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: '#ef5350',
                '&:hover': { backgroundColor: '#e53935' },
              }}
            >
              {t('desktop.disconnect')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Display Area */}
      <Box
        ref={displayRef}
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#000',
        }}
      >
        {connectionState !== 'connected' && (
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.1)',
              maxWidth: 540,
              width: '100%',
            }}
          >
            <CloudIcon
              sx={{ fontSize: 80, color: '#3949ab', mb: 2, opacity: 0.8 }}
            />
            <Typography
              variant="h5"
              sx={{ color: '#e3f2fd', fontWeight: 600, mb: 1.5 }}
            >
              {connectionState === 'connecting'
                ? t('desktop.connecting')
                : t('desktop.title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#90a4ae', mb: 3, lineHeight: 1.6 }}
            >
              {connectionState === 'connecting'
                ? t('desktop.connectingMessage')
                : t('desktop.connectMessage')}
            </Typography>

            {/* Settings Panel - visible when not connecting */}
            {connectionState === 'disconnected' && (
              <Box sx={{ mt: 3, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SettingsIcon sx={{ color: '#64b5f6', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: '#bbdefb', fontWeight: 600 }}>
                    {t('desktop.settings')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {/* Resolution */}
                  <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                    <InputLabel sx={{ color: '#78909c' }}>{t('desktop.resolution')}</InputLabel>
                    <Select
                      value={rdpSettings.resolution}
                      label={t('desktop.resolution')}
                      onChange={(e) => updateSettings({ resolution: e.target.value })}
                      sx={{
                        color: '#e0e0e0',
                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#64b5f6' },
                        '.MuiSvgIcon-root': { color: '#78909c' },
                      }}
                    >
                      {RESOLUTION_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.value === 'auto' ? t(`desktop.${opt.label}`) : opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Quality */}
                  <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                    <InputLabel sx={{ color: '#78909c' }}>{t('desktop.quality')}</InputLabel>
                    <Select
                      value={rdpSettings.quality}
                      label={t('desktop.quality')}
                      onChange={(e) => updateSettings({ quality: e.target.value })}
                      sx={{
                        color: '#e0e0e0',
                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#64b5f6' },
                        '.MuiSvgIcon-root': { color: '#78909c' },
                      }}
                    >
                      <MenuItem value="high">{t('desktop.qualityHigh')}</MenuItem>
                      <MenuItem value="medium">{t('desktop.qualityMedium')}</MenuItem>
                      <MenuItem value="low">{t('desktop.qualityLow')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  {/* Audio Toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={rdpSettings.audio}
                        onChange={(e) => updateSettings({ audio: e.target.checked })}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#64b5f6' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#64b5f6' },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: '#b0bec5' }}>
                        {t('desktop.audio')}
                      </Typography>
                    }
                  />

                  {/* Wallpaper Toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={rdpSettings.wallpaper}
                        onChange={(e) => updateSettings({ wallpaper: e.target.checked })}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#64b5f6' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#64b5f6' },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: '#b0bec5' }}>
                        {t('desktop.wallpaper')}
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            )}

            {connectionInfo && (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 3 }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#78909c', display: 'block' }}>
                    {t('desktop.server')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#bbdefb', fontFamily: 'monospace', fontWeight: 600 }}
                  >
                    {connectionInfo.serverIp}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#78909c', display: 'block' }}>
                    {t('desktop.port')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#bbdefb', fontFamily: 'monospace', fontWeight: 600 }}
                  >
                    {connectionInfo.rdpPort}
                  </Typography>
                </Box>
              </Box>
            )}
            {connectionState === 'connecting' && (
              <CircularProgress
                size={36}
                sx={{ color: '#64b5f6', mt: 3 }}
              />
            )}
          </Paper>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Desktop;
